import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { VideoPlayer } from '@/components/custom/VideoPlayer';
import { ChatInterface } from '@/components/custom/ChatInterface';
import { LiveForm } from '@/components/custom/LiveForm';
import { SignableDocument } from '@/components/custom/SignableDocument';
import { DocumentUpload } from '@/components/custom/DocumentUpload';
import { toast } from 'sonner';
import { 
  Loader2, 
  CheckCircle2, 
  FileText, 
  PenTool, 
  Upload, 
  CreditCard,
  MessageSquare,
  ChevronRight,
  Download,
  PartyPopper,
  Clock,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Video URL from uploaded asset
const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_1c95368b-d676-42f7-9de3-3c96ff44933a/artifacts/xme5t6la_claims_submission%20%28540p%29.mp4';
const VIDEO_POSTER = 'https://images.pexels.com/photos/7876087/pexels-photo-7876087.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1';

// Storage keys for persistence
const STORAGE_KEY = 'quantum_intake_session';
const DEBOUNCE_MS = 2000;

const STEPS = [
  { id: 'intake', label: 'Fill Out Intake', icon: MessageSquare },
  { id: 'upload', label: 'Upload Documents', icon: Upload },
  { id: 'sign', label: 'Authorize', icon: PenTool },
  { id: 'payment', label: 'Payment', icon: CreditCard },
  { id: 'confirmation', label: 'Confirmation', icon: CheckCircle2 },
];

// =================== COUNTDOWN COMPONENT ===================
function SessionCountdown({ expiresAt, onExpired }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!expiresAt) return;

    const updateCountdown = () => {
      const now = Date.now();
      const expires = new Date(expiresAt).getTime();
      const diff = expires - now;

      if (diff <= 0) {
        setTimeLeft(null);
        onExpired?.();
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds, total: diff });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, onExpired]);

  if (!timeLeft) return null;

  const isWarning = timeLeft.total < 10 * 60 * 1000; // Less than 10 minutes
  const isCritical = timeLeft.total < 5 * 60 * 1000; // Less than 5 minutes

  return (
    <div 
      className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full ${
        isCritical 
          ? 'bg-red-100 text-red-700' 
          : isWarning 
            ? 'bg-yellow-100 text-yellow-700' 
            : 'bg-gray-100 text-gray-600'
      }`}
    >
      <Clock className="w-3 h-3" />
      <span className="font-mono">
        {String(timeLeft.hours).padStart(2, '0')}:
        {String(timeLeft.minutes).padStart(2, '0')}:
        {String(timeLeft.seconds).padStart(2, '0')}
      </span>
      {isCritical && <span className="text-xs">remaining</span>}
    </div>
  );
}

// =================== MAIN COMPONENT ===================
export default function IntakePage() {
  const navigate = useNavigate();
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({});
  const [lastUpdatedField, setLastUpdatedField] = useState(null);
  const [currentStep, setCurrentStep] = useState('intake');
  const [intakeId, setIntakeId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [signatureSavedAt, setSignatureSavedAt] = useState(null);
  const [documentsUploaded, setDocumentsUploaded] = useState(false);
  const [audioActivated, setAudioActivated] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [intakeStepComplete, setIntakeStepComplete] = useState(false);
  
  // Payment popup state
  const [paymentPopupOpened, setPaymentPopupOpened] = useState(false);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [selectedPaymentUrl, setSelectedPaymentUrl] = useState(null);
  const [selectedPaymentAmount, setSelectedPaymentAmount] = useState(null);
  
  // Refs
  const activationRef = useRef(false);
  const pendingChangesRef = useRef({});
  const debounceTimerRef = useRef(null);
  const isInitializedRef = useRef(false);
  const paymentWindowRef = useRef(null);

  // Handler for when voice agent determines step is complete
  const handleStepComplete = useCallback((stepStatus) => {
    if (stepStatus.stepComplete) {
      setIntakeStepComplete(true);
    }
  }, []);

  // =================== SESSION MANAGEMENT ===================
  
  // Create new session on backend
  const createSession = async (initialData = {}) => {
    try {
      const response = await axios.post(`${API}/intake/sessions`, {
        initial_data: initialData
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  };

  // Get existing session from backend
  const getSession = async (id) => {
    try {
      const response = await axios.get(`${API}/intake/sessions/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Session expired or not found
      }
      throw error;
    }
  };

  // Patch session (debounced)
  const patchSession = async (id, data, step) => {
    try {
      setIsSaving(true);
      const response = await axios.patch(`${API}/intake/sessions/${id}`, {
        data: data,
        current_step: step
      });
      setExpiresAt(response.data.expiresAt);
      return response.data;
    } catch (error) {
      console.error('Failed to patch session:', error);
      // Don't throw - we don't want to wipe user data on save failures
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  // Save signature to session
  const saveSignatureToSession = async (id, dataUrl) => {
    try {
      const response = await axios.post(`${API}/intake/sessions/${id}/signature`, {
        signature_data_url: dataUrl
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save signature:', error);
      throw error;
    }
  };

  // =================== INITIALIZATION ===================
  
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initializeSession = async () => {
      setIsSessionLoading(true);
      setSessionError(null);

      // Retry function for network resilience
      const retryOperation = async (operation, maxRetries = 3) => {
        for (let i = 0; i < maxRetries; i++) {
          try {
            return await operation();
          } catch (error) {
            console.warn(`Attempt ${i + 1} failed:`, error.message);
            if (i === maxRetries - 1) throw error;
            await new Promise(r => setTimeout(r, 1000 * (i + 1)));
          }
        }
      };

      try {
        // Check localStorage for existing session data (even if backend fails)
        const storedSession = localStorage.getItem(STORAGE_KEY);
        let localFormData = {};
        let localStep = 'intake';
        
        if (storedSession) {
          try {
            const parsed = JSON.parse(storedSession);
            localFormData = parsed.formData || {};
            localStep = parsed.currentStep || 'intake';
            
            if (parsed.sessionId) {
              // Try to restore from backend
              try {
                const existingSession = await retryOperation(() => getSession(parsed.sessionId));
                
                if (existingSession) {
                  console.log('Restored session from backend:', existingSession);
                  setSessionId(existingSession.sessionId);
                  setExpiresAt(existingSession.expiresAt);
                  setFormData(existingSession.data || localFormData);
                  setCurrentStep(existingSession.currentStep || localStep);
                  setSignatureData(existingSession.signature?.dataUrl || null);
                  if (existingSession.signaturePresent) {
                    setSignatureSavedAt(existingSession.signature?.signedAt);
                  }
                  toast.info('Your previous progress has been restored');
                  setIsSessionLoading(false);
                  return;
                }
              } catch (restoreError) {
                console.warn('Could not restore session from backend, using local data');
                // Fall through to create new session but preserve local data
              }
            }
          } catch (parseError) {
            console.warn('Could not parse stored session');
          }
        }

        // No valid session found, create new one
        try {
          const newSession = await retryOperation(() => createSession(localFormData));
          console.log('Created new session:', newSession);
          setSessionId(newSession.sessionId);
          setExpiresAt(newSession.expiresAt);
          setFormData(newSession.data || localFormData);
          setCurrentStep(newSession.currentStep || localStep);
          
          // Store session ID in localStorage with preserved data
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            sessionId: newSession.sessionId,
            formData: newSession.data || localFormData,
            currentStep: newSession.currentStep || localStep
          }));
          
        } catch (createError) {
          console.error('Failed to create session:', createError);
          
          // Fallback: Work in offline mode with localStorage only
          const fallbackSessionId = `local_${Date.now()}`;
          setSessionId(fallbackSessionId);
          setFormData(localFormData);
          setCurrentStep(localStep);
          setExpiresAt(new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString());
          
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            sessionId: fallbackSessionId,
            formData: localFormData,
            currentStep: localStep
          }));
          
          toast.warning('Working in offline mode. Your progress will be saved locally.');
        }

      } catch (error) {
        console.error('Session initialization error:', error);
        
        // Even on error, let user continue with local storage
        const fallbackSessionId = `local_${Date.now()}`;
        setSessionId(fallbackSessionId);
        setFormData({});
        setCurrentStep('intake');
        setExpiresAt(new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString());
        
        toast.warning('Connection issue. Working in offline mode.');
      } finally {
        setIsSessionLoading(false);
      }
    };

    initializeSession();
  }, []);

  // =================== DEBOUNCED SAVE ===================
  
  const flushPendingChanges = useCallback(async () => {
    if (!sessionId || Object.keys(pendingChangesRef.current).length === 0) return;
    
    const changesToSave = { ...pendingChangesRef.current };
    pendingChangesRef.current = {};
    
    await patchSession(sessionId, changesToSave, currentStep);
    
    // Update localStorage backup
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      sessionId,
      formData: { ...formData, ...changesToSave },
      currentStep
    }));
  }, [sessionId, formData, currentStep]);

  const scheduleSave = useCallback((newData) => {
    // Accumulate changes
    pendingChangesRef.current = { ...pendingChangesRef.current, ...newData };
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Schedule new save
    debounceTimerRef.current = setTimeout(() => {
      flushPendingChanges();
    }, DEBOUNCE_MS);
  }, [flushPendingChanges]);

  // Flush on unmount or step change
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      flushPendingChanges();
    };
  }, [flushPendingChanges]);

  // =================== SESSION EXPIRY ===================
  
  const handleSessionExpired = useCallback(() => {
    toast.error('Your session has expired. Please start a new intake.');
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  }, []);

  // =================== AUDIO ACTIVATION ===================
  
  const handleActivation = useCallback(() => {
    if (!activationRef.current) {
      activationRef.current = true;
      setAudioActivated(true);
      console.log('Audio activated by user');
    }
  }, []);

  useEffect(() => {
    const activate = (e) => {
      if (!activationRef.current) {
        console.log('Activation event:', e.type);
        handleActivation();
      }
    };

    const events = [
      'click', 'mousedown', 'mouseup',
      'touchstart', 'touchend',
      'keydown', 'keyup',
      'pointerdown', 'pointerup',
      'contextmenu', 'auxclick', 'dblclick'
    ];
    
    events.forEach(event => {
      document.addEventListener(event, activate, { capture: true, passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, activate, { capture: true });
      });
    };
  }, [handleActivation]);

  // =================== FORM HANDLERS ===================
  
  const handleFieldUpdate = useCallback((update) => {
    setFormData(prev => {
      const newData = { ...prev, ...update };
      // Schedule debounced save to backend
      scheduleSave(update);
      return newData;
    });
    
    const fieldKey = Object.keys(update)[0];
    setLastUpdatedField(fieldKey);
    setTimeout(() => setLastUpdatedField(null), 1000);
  }, [scheduleSave]);

  const handleFormComplete = useCallback(() => {
    toast.success('Form complete! Please proceed to upload documents.');
  }, []);

  const submitIntake = async () => {
    setIsSubmitting(true);
    try {
      // Flush any pending changes first
      await flushPendingChanges();
      
      const response = await axios.post(`${API}/intake/submit`, {
        form_data: formData,
        session_id: sessionId,
        signature_data: signatureData
      }, { timeout: 30000 });
      
      setIntakeId(response.data.intake_id);
      toast.success('Intake form saved successfully');
      return response.data.intake_id;
    } catch (error) {
      console.error('Submit error:', error);
      
      let errorMsg = 'Failed to save intake form. ';
      if (error.code === 'ECONNABORTED') {
        errorMsg += 'Connection timed out. Please try again.';
      } else if (!navigator.onLine) {
        errorMsg += 'You appear to be offline. Please check your connection.';
      } else {
        errorMsg += 'Please try again.';
      }
      
      toast.error(errorMsg);
      // Data is still in localStorage, so don't panic
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignatureSave = async (dataUrl) => {
    setIsSubmitting(true);
    
    try {
      // Save signature data locally first (so it's not lost)
      setSignatureData(dataUrl);
      
      // First save signature to session (this must succeed before allowing Next)
      if (sessionId) {
        try {
          const sigResult = await saveSignatureToSession(sessionId, dataUrl);
          if (sigResult?.ok) {
            setSignatureSavedAt(sigResult.signedAt);
            setExpiresAt(sigResult.expiresAt);
          }
        } catch (sessionError) {
          console.error('Session signature save failed:', sessionError);
          // Continue - we'll save to main intake record
        }
      }
      
      // Create intake if not already created
      let id = intakeId;
      if (!id) {
        id = await submitIntake();
        if (!id) {
          // Retry once
          await new Promise(r => setTimeout(r, 1000));
          id = await submitIntake();
          if (!id) {
            toast.error('Unable to save. Your authorization has been captured locally. Please try the button again.');
            setIsSubmitting(false);
            return;
          }
        }
      }
      
      // Save signature to main intake record
      try {
        const response = await axios.post(`${API}/intake/${id}/signature`, {
          intake_id: id,
          signature_base64: dataUrl
        }, { timeout: 30000 });
        
        if (response.data.success) {
          toast.success('Authorization saved successfully!');
          
          // Update step
          setCurrentStep('payment');
          
          // Save step change to backend (don't block on this)
          if (sessionId) {
            patchSession(sessionId, {}, 'payment').catch(console.error);
          }
        } else {
          throw new Error('Authorization save failed');
        }
      } catch (signatureError) {
        console.error('Signature save error:', signatureError);
        // Authorization is saved locally, let them proceed
        toast.warning('Authorization captured. Proceeding to payment...');
        setCurrentStep('payment');
      }
    } catch (error) {
      console.error('Authorization error:', error);
      toast.error('An error occurred. Your authorization has been captured. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinalSubmission = async (paymentOption, paymentReference) => {
    setIsSubmitting(true);
    
    let driveUrl = null;
    let driveSuccess = false;
    let paymentSuccess = false;
    
    try {
      // Create Google Drive folder, upload docs, send to CRM
      try {
        const driveResponse = await axios.post(`${API}/google-drive/create`, {
          intake_id: intakeId,
          form_data: formData
        }, { timeout: 60000 });
        
        driveUrl = driveResponse.data.folder_url;
        driveSuccess = true;
      } catch (driveError) {
        console.error('Drive upload error:', driveError);
        // Continue - don't block the user, files can be uploaded manually
        toast.warning('Document upload encountered an issue. You can email documents to AspenLegal@USA.com');
      }
      
      // Mark payment
      try {
        await axios.post(`${API}/intake/${intakeId}/payment`, {
          intake_id: intakeId,
          payment_option: paymentOption,
          payment_reference: paymentReference
        }, { timeout: 30000 });
        paymentSuccess = true;
      } catch (paymentError) {
        console.error('Payment record error:', paymentError);
        // Continue - payment was made externally via Stripe
      }
      
      // Set submission result for confirmation page
      setSubmissionResult({
        success: true,
        intakeId: intakeId,
        folderUrl: driveUrl,
        clientName: formData.name,
        email: formData.email,
        signedAt: signatureSavedAt,
        paymentOption: paymentOption,
        driveUploaded: driveSuccess,
        paymentRecorded: paymentSuccess
      });
      
      setPaymentConfirmed(true);
      
      // Clear session since we're done
      localStorage.removeItem(STORAGE_KEY);
      
      // Move to confirmation step
      setCurrentStep('confirmation');
      
      if (driveSuccess && paymentSuccess) {
        toast.success('Submission complete!');
      } else {
        toast.success('Submission recorded! Some background tasks are still processing.');
      }
      
    } catch (error) {
      console.error('Final submission error:', error);
      toast.error('Failed to complete submission. Your data has been saved - please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // =================== STRIPE PAYMENT POPUP HANDLERS ===================
  
  const [stripePaymentId, setStripePaymentId] = useState(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  
  const openPaymentPopup = async (option, fallbackUrl, amount) => {
    // Store payment details
    setSelectedPaymentOption(option);
    setSelectedPaymentAmount(amount);
    setIsCreatingCheckout(true);
    
    try {
      // Call Stripe API to create checkout session with all available client info
      const response = await axios.post(`${API}/stripe/create-checkout`, {
        customer_email: formData.email || '',
        customer_name: formData.name || '',
        customer_phone: formData.phone || '',
        customer_address: formData.address || '',
        payment_type: option,
        intake_id: intakeId,
        session_id: sessionId
      });
      
      if (response.data.success) {
        const checkoutUrl = response.data.checkout_url;
        setStripePaymentId(response.data.payment_id);
        setSelectedPaymentUrl(checkoutUrl);
        
        // Open popup window with Stripe Checkout
        const width = 650;
        const height = 800;
        const left = (window.innerWidth - width) / 2;
        const top = (window.innerHeight - height) / 2;
        
        paymentWindowRef.current = window.open(
          checkoutUrl,
          'StripePayment',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no`
        );
        
        setPaymentPopupOpened(true);
        toast.info('Stripe payment window opened. Complete your payment and return here.');
        
        // Start polling for popup close
        pollPopupStatus();
        
      } else {
        toast.error(response.data.error || 'Failed to create payment session');
        // Fallback to direct URL
        openPaymentPopupFallback(option, fallbackUrl, amount);
      }
      
    } catch (error) {
      console.error('Error creating Stripe checkout:', error);
      toast.error('Failed to initialize payment. Using backup payment link.');
      // Fallback to direct URL on error
      openPaymentPopupFallback(option, fallbackUrl, amount);
    } finally {
      setIsCreatingCheckout(false);
    }
  };
  
  const openPaymentPopupFallback = (option, url, amount) => {
    // Fallback function using direct Stripe payment links
    setSelectedPaymentOption(option);
    setSelectedPaymentUrl(url);
    setSelectedPaymentAmount(amount);
    
    const width = 650;
    const height = 800;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    
    paymentWindowRef.current = window.open(
      url,
      'StripePayment',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no`
    );
    
    setPaymentPopupOpened(true);
    toast.info('Payment window opened. Complete your payment and return here.');
  };
  
  const pollPopupStatus = () => {
    // Poll to check if popup is closed
    const checkPopup = setInterval(() => {
      if (paymentWindowRef.current && paymentWindowRef.current.closed) {
        clearInterval(checkPopup);
        // Popup was closed, but don't auto-confirm - wait for user to click confirm button
      }
    }, 1000);
    
    // Stop polling after 30 minutes
    setTimeout(() => clearInterval(checkPopup), 30 * 60 * 1000);
  };

  const confirmPaymentCompleted = async () => {
    if (!selectedPaymentOption) {
      toast.error('Please select a payment option first');
      return;
    }
    
    setIsSubmitting(true);
    
    // Close popup if still open
    if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
      paymentWindowRef.current.close();
    }
    
    try {
      // If we have a Stripe payment ID, confirm it via the API
      if (stripePaymentId) {
        await axios.post(`${API}/stripe/confirm-payment`, {
          payment_id: stripePaymentId
        });
      } else {
        // Fallback: Send notification via old endpoint
        await axios.post(`${API}/notify-payment-confirmation`, {
          client_name: formData.name || 'Unknown Client',
          client_email: formData.email || '',
          payment_option: selectedPaymentOption,
          intake_id: intakeId
        });
      }
      
      toast.success('Payment confirmed!');
      
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      // Don't block the flow if confirmation fails - still proceed
    }
    
    // Proceed with final submission
    await handleFinalSubmission(selectedPaymentOption, stripePaymentId || 'confirmed_by_user');
    setIsSubmitting(false);
  };

  const handleDocumentsUploaded = () => {
    setDocumentsUploaded(true);
    toast.success('Documents uploaded! Please proceed to authorize.');
  };

  const handleNextStep = async () => {
    const stepIndex = STEPS.findIndex(s => s.id === currentStep);
    if (stepIndex < STEPS.length - 1) {
      const nextStep = STEPS[stepIndex + 1].id;
      
      // If moving to upload step, create intake first
      if (nextStep === 'upload' && !intakeId) {
        const id = await submitIntake();
        if (!id) return;
      }
      
      setCurrentStep(nextStep);
      
      // Save step to backend
      if (sessionId) {
        await patchSession(sessionId, {}, nextStep);
      }
    }
  };

  const filledFieldsCount = Object.keys(formData).filter(k => formData[k]).length;
  const canProceedFromIntake = filledFieldsCount >= 5;

  // =================== LOADING STATE ===================
  
  if (isSessionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">Initializing your intake session...</p>
        </div>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Session Error</h2>
          <p className="text-gray-500 mb-4">{sessionError}</p>
          <Button onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  // =================== RENDER ===================
  
  return (
    <div className="min-h-screen bg-white" data-testid="intake-page">
      {/* Click prompt overlay */}
      {!audioActivated && (
        <div 
          className="fixed inset-0 z-[9999] cursor-pointer flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.3)' }}
          onClick={handleActivation}
          onMouseDown={handleActivation}
          onTouchStart={handleActivation}
          onPointerDown={handleActivation}
          data-testid="audio-activation-overlay"
        >
          <div className="bg-white px-8 py-6 rounded-2xl shadow-2xl text-center max-w-sm mx-4">
            <div className="w-4 h-4 bg-green-500 rounded-full mx-auto mb-3 animate-ping"></div>
            <p className="text-lg font-medium text-gray-800 mb-1">Ready to Begin</p>
            <p className="text-sm text-gray-500">Click to start your AI assisted intake form completion</p>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a 
              href="https://toddproffit.com"
              className="text-2xl font-bold tracking-tight hover:opacity-80 transition-opacity cursor-pointer"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="header-title"
            >
              ToddProffit
            </a>
            <div className="flex items-center gap-4">
              {/* Session countdown */}
              {currentStep !== 'confirmation' && expiresAt && (
                <SessionCountdown 
                  expiresAt={expiresAt} 
                  onExpired={handleSessionExpired}
                />
              )}
              {/* Saving indicator */}
              {isSaving && (
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Saving...</span>
                </div>
              )}
              {/* Field count */}
              <div className="text-sm text-muted-foreground">
                {filledFieldsCount > 0 && `${filledFieldsCount}/12 fields`}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between overflow-x-auto">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const stepIndex = STEPS.findIndex(s => s.id === currentStep);
              const isCompleted = index < stepIndex;
              
              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => {
                      if (isCompleted || isActive) setCurrentStep(step.id);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 transition-colors whitespace-nowrap ${
                      isActive 
                        ? 'text-black font-medium' 
                        : isCompleted 
                          ? 'text-green-600' 
                          : 'text-muted-foreground'
                    }`}
                    data-testid={`step-${step.id}`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                    <span className="hidden sm:inline">{step.label}</span>
                  </button>
                  {index < STEPS.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Step 1: Fill Out Intake */}
          {currentStep === 'intake' && (
            <section data-testid="intake-section">
              <div className="mb-6">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Complete Your Intake
                </h2>
                <p className="text-muted-foreground">
                  Talk to our AI assistant or fill out the form directly. Watch the video below for more information.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                      AI Voice Assistant
                    </span>
                  </div>
                  <ChatInterface
                    sessionId={sessionId}
                    formData={formData}
                    onFieldUpdate={handleFieldUpdate}
                    onFormComplete={handleFormComplete}
                    onStepComplete={handleStepComplete}
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                      Intake Form (9 Pages) - Fillable
                    </span>
                  </div>
                  <div className="border border-gray-200 p-6 max-h-[700px] overflow-y-auto">
                    <LiveForm 
                      formData={formData}
                      lastUpdatedField={lastUpdatedField}
                      onFieldChange={handleFieldUpdate}
                    />
                  </div>
                </div>
              </div>

              {canProceedFromIntake && (
                <div className="mt-8 text-center">
                  <Button
                    onClick={handleNextStep}
                    disabled={isSubmitting}
                    className={`rounded-none px-8 py-6 text-lg transition-all ${
                      intakeStepComplete 
                        ? 'bg-green-600 hover:bg-green-700 text-white animate-pulse ring-4 ring-green-300' 
                        : 'bg-black text-white hover:bg-black/90'
                    }`}
                    data-testid="proceed-upload-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        {intakeStepComplete ? 'Click Here → ' : ''}Continue to Document Upload
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                  {intakeStepComplete && (
                    <p className="mt-2 text-sm text-green-700 font-medium animate-bounce">
                      ↑ Voice Agent: All fields complete! Verify the form, then click NEXT above.
                    </p>
                  )}
                </div>
              )}

              <div className="mt-12">
                <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Learn About Our Claims Process
                </h3>
                <VideoPlayer 
                  src={VIDEO_URL}
                  poster={VIDEO_POSTER}
                />
              </div>
            </section>
          )}

          {/* Step 2: Upload Documents */}
          {currentStep === 'upload' && intakeId && (
            <section data-testid="upload-section" className="max-w-2xl mx-auto">
              <div className="mb-8">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Upload Documents
                </h2>
                <p className="text-muted-foreground">
                  Please upload your identification and case documents to continue.
                </p>
              </div>

              <DocumentUpload 
                intakeId={intakeId}
                onUploadComplete={() => {
                  handleDocumentsUploaded();
                  setCurrentStep('sign');
                }}
              />
            </section>
          )}

          {/* Step 3: Sign */}
          {currentStep === 'sign' && (
            <section data-testid="sign-section" className="max-w-4xl mx-auto">
              <div className="mb-8">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Review & Authorize Contract
                </h2>
                <p className="text-muted-foreground">
                  Please review your information below and authorize your intake submission.
                </p>
              </div>

              <SignableDocument 
                formData={formData}
                onSign={handleSignatureSave}
                disabled={isSubmitting}
              />

              {signatureData && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-green-800">Authorization captured successfully</span>
                </div>
              )}
            </section>
          )}

          {/* Step 4: Payment */}
          {currentStep === 'payment' && (
            <section data-testid="payment-section" className="max-w-2xl mx-auto">
              <div className="mb-8 text-center">
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Ready for Payment
                </h2>
                <p className="text-muted-foreground">
                  Your intake form and authorization have been saved. Please select a payment option to complete your submission.
                </p>
              </div>

              <div className="bg-green-50 p-4 border border-green-200 mb-6 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800">Authorization Verified</p>
                  <p className="text-sm text-green-700">Your authorization was captured and saved securely.</p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 border border-gray-200 mb-8">
                <h3 className="font-semibold mb-2">Deposit Fee: $2,500</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  If we cannot assist with your case, your deposit will be refunded the same day.
                </p>
                <p className="text-xs text-muted-foreground">
                  Choose the payment option that works best for you.
                </p>
              </div>

              {/* Payment Options - Before popup opened */}
              {!paymentPopupOpened && (
                <div className="space-y-4">
                  {isCreatingCheckout && (
                    <div className="bg-indigo-50 border border-indigo-200 p-4 text-center rounded">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-600" />
                      <p className="text-sm text-indigo-700">Creating secure Stripe checkout...</p>
                    </div>
                  )}
                  
                  <button
                    onClick={() => openPaymentPopup('one_time', 'https://buy.stripe.com/8x214o3wB9qJasscz93oA00', '$2,500')}
                    disabled={isSubmitting || isCreatingCheckout}
                    className="w-full p-4 border-2 border-gray-200 hover:border-indigo-500 hover:bg-indigo-50 transition-colors text-left flex justify-between items-center disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                    data-testid="payment-one-time"
                  >
                    <div>
                      <p className="font-semibold">One-Time Payment</p>
                      <p className="text-sm text-muted-foreground">$2,500 - Pay in full</p>
                    </div>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
                    <svg className="w-4 h-4" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#635BFF" d="M32 16.125C32 7.22 24.837 0 16 0S0 7.22 0 16.125c0 7.98 5.837 14.594 13.5 15.77v-11.16H9.437v-4.61H13.5v-3.515c0-4.005 2.388-6.218 6.038-6.218 1.75 0 3.575.312 3.575.312v3.938h-2.013c-1.987 0-2.6 1.232-2.6 2.495v3h4.438l-.713 4.608H18.5v11.16C26.163 30.72 32 24.104 32 16.126z"/>
                    </svg>
                    <span>Secure payments powered by</span>
                    <svg className="h-5" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#635BFF" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a12.4 12.4 0 01-4.56.8c-4.18 0-6.98-2.25-6.98-6.73 0-3.8 2.4-6.73 6.3-6.73 3.83 0 6.05 2.93 6.05 7.74zm-4.39-1.35c.92 0 1.85-.62 1.85-2.17 0-1.56-.93-2.17-1.85-2.17-.93 0-1.86.61-1.86 2.17 0 1.55.93 2.17 1.86 2.17zm-8.49-5.85h4.3v12.6h-4.3v-.96c-.8.8-1.84 1.28-3.27 1.28-2.92 0-5.2-2.48-5.2-6.62 0-4.13 2.28-6.62 5.2-6.62 1.43 0 2.47.48 3.27 1.28v-.96zm-2.52 9.5c1.33 0 2.52-1.05 2.52-3.2 0-2.16-1.19-3.21-2.52-3.21-1.34 0-2.52 1.05-2.52 3.2 0 2.16 1.18 3.21 2.52 3.21zm-7.58-9.5h4.3v12.6h-4.3v-12.6zm-4.78 4.2h-2.44v4.3c0 1.04.7 1.35 1.35 1.35.43 0 .8-.07 1.09-.2v3.14c-.5.27-1.27.47-2.35.47-2.48 0-4.39-1.23-4.39-4.22v-4.83h-1.8V7.08h1.8V4.23l4.3-1.27v4.12h2.44v3.1zm-13.28-.95c0-.8-.52-1.27-1.41-1.27-.78 0-1.54.39-2.23.97v8.3h-4.3V7.08h4.3v1.13c.97-.85 2.18-1.45 3.67-1.45 2.35 0 4.27 1.35 4.27 4.26v8.66h-4.3v-7.5zm-10.35-.75c-1.33 0-2.18.73-2.18 1.87 0 1.13.85 1.87 2.18 1.87 1.32 0 2.17-.74 2.17-1.87 0-1.14-.85-1.87-2.17-1.87zm2.17 8.25v-1.16c-.8.88-1.98 1.48-3.56 1.48-2.64 0-4.69-1.6-4.69-4.3 0-2.71 2.05-4.3 4.69-4.3 1.58 0 2.76.6 3.56 1.48V11.8c0-1.05-.78-1.9-2.25-1.9-1.22 0-2.39.42-3.51 1.18V7.48a11.07 11.07 0 014.17-.92c3.13 0 5.33 1.56 5.33 4.76v8.36h-3.74z"/>
                    </svg>
                  </div>
                </div>
              )}

              {/* Payment Confirmation - After popup opened */}
              {paymentPopupOpened && (
                <div className="space-y-6">
                  <div className="bg-indigo-50 border border-indigo-200 p-6 text-center rounded-lg">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h3 className="font-semibold text-indigo-800 mb-2">Stripe Payment Window Opened</h3>
                    <p className="text-sm text-indigo-700 mb-2">
                      Complete your <strong>{selectedPaymentOption?.replace('_', ' ')}</strong> payment of <strong>{selectedPaymentAmount}</strong> in the Stripe checkout window.
                    </p>
                    <p className="text-xs text-indigo-600">
                      If the window didn't open, <button onClick={() => window.open(selectedPaymentUrl, 'StripePayment', 'width=650,height=800')} className="underline font-medium">click here to open it</button>.
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> After completing your payment in the Stripe window, return here and click the button below to continue.
                    </p>
                  </div>

                  <Button
                    onClick={() => confirmPaymentCompleted()}
                    disabled={isSubmitting}
                    className="w-full rounded-none bg-green-600 text-white hover:bg-green-700 py-6 text-lg"
                    data-testid="confirm-payment-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        I've Completed My Payment - Continue
                      </>
                    )}
                  </Button>

                  <button
                    onClick={() => {
                      setPaymentPopupOpened(false);
                      setSelectedPaymentOption(null);
                      setSelectedPaymentUrl(null);
                      setSelectedPaymentAmount(null);
                    }}
                    className="w-full text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Choose a different payment option
                  </button>
                </div>
              )}

              {isSubmitting && !paymentPopupOpened && (
                <div className="mt-6 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Processing your submission...</p>
                </div>
              )}
            </section>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 'confirmation' && submissionResult && (
            <section data-testid="confirmation-section" className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <PartyPopper className="w-10 h-10 text-green-600" />
                </div>
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Submission Complete!
                </h2>
                <p className="text-muted-foreground">
                  Thank you, {submissionResult.clientName}. Your intake form has been successfully submitted.
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 p-6 mb-6">
                <h3 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  What Happens Next
                </h3>
                <ul className="space-y-2 text-green-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-1 flex-shrink-0" />
                    <span>Your signed contract has been saved to your secure folder</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-1 flex-shrink-0" />
                    <span>A confirmation email will be sent to {submissionResult.email}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-1 flex-shrink-0" />
                    <span>Our team will contact you within 24-48 business hours</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 p-6 mb-6">
                <h3 className="font-semibold mb-4">Submission Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Reference ID</p>
                    <p className="font-mono">{submissionResult.intakeId}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Signed On</p>
                    <p>{submissionResult.signedAt ? new Date(submissionResult.signedAt).toLocaleString() : 'Just now'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Payment Plan</p>
                    <p className="capitalize">{submissionResult.paymentOption?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <p className="text-green-600 font-medium">Confirmed</p>
                  </div>
                </div>
              </div>

              {/* Signed Document Preview */}
              <div className="border border-gray-200 mb-6">
                <div className="bg-gray-100 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Your Signed Contract
                  </h3>
                  {submissionResult.folderUrl && (
                    <a 
                      href={submissionResult.folderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Download className="w-4 h-4" />
                      View in Drive
                    </a>
                  )}
                </div>
                <div className="p-4 max-h-96 overflow-y-auto">
                  <SignableDocument 
                    formData={formData}
                    readOnly={true}
                    signatureData={signatureData}
                  />
                </div>
              </div>

              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  Questions? Contact us:
                </p>
                <div className="flex justify-center gap-6 text-sm">
                  <div>
                    <p className="font-medium">Christine (Accounts)</p>
                    <p className="text-muted-foreground">970-846-3088</p>
                  </div>
                  <div>
                    <p className="font-medium">Todd (Case Discussion)</p>
                    <p className="text-muted-foreground">970-710-9193</p>
                  </div>
                </div>
                
                <Button
                  onClick={() => window.location.href = 'https://toddproffit.com'}
                  variant="outline"
                  className="mt-4"
                >
                  Return to ToddProffit.com
                </Button>
              </div>
            </section>
          )}

        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Quantum LAW Consulting LLC. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Page, Automations & Ai Voice Agent- Designed, Coded and Maintained by UNLIMITED, LLC.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            This intake form does not constitute legal advice. Please consult with a qualified professional.
          </p>
        </div>
      </footer>
    </div>
  );
}
